import { useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { CloseIcon } from '@/components/icons'
import { dialogReducedVariants, dialogVariants, overlayVariants, usePrefersReducedMotion } from '@/design-system/motion'
import { IconButton } from './IconButton'
import { useOverlayBehaviour } from './internal/use-overlay'
import './Dialog.css'

export type DialogProps = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
}

/** Centered modal dialog with visible close, Escape and overlay dismissal. */
export function Dialog({ open, onClose, title, children, footer }: DialogProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const reducedMotion = usePrefersReducedMotion()
  useOverlayBehaviour(open, onClose)

  useEffect(() => {
    if (open) dialogRef.current?.focus()
  }, [open])

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="ui-dialog-root">
          <motion.div
            className="ui-dialog-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            className="ui-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            ref={dialogRef}
            variants={reducedMotion ? dialogReducedVariants : dialogVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="ui-dialog__header">
              <h2 className="ui-dialog__title" id={titleId}>
                {title}
              </h2>
              <IconButton aria-label="Sluiten" onClick={onClose}>
                <CloseIcon size={20} />
              </IconButton>
            </div>
            <div className="ui-dialog__body">{children}</div>
            {footer && <div className="ui-dialog__footer">{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
